// Fill-amount maths shared by the dispatch getters.
//
// A site's delivery requirement is a RANGE, not one figure:
//     minFillAmount = max(0, capacity × minFillPct/100 − volume)   (to minimum)
//     fillAmount    = max(0, capacity × maxFillPct/100 − volume)   (to maximum)
//
// `predictedFillAmount` is what it will need ON THE DISPATCH DATE. The site's
// Injection Avg ages the volume forward to that date:
//     predictedVolume     = max(0, volume − injectionAvg × daysUntilDispatch)
//     predictedFillAmount = max(0, capacity × maxFillPct/100 − predictedVolume)
//
// ASSUMPTION: Injection Avg is litres per day. That's the same basis as DTE
// (days to empty) in this dataset — DTE = volume / Injection Avg — and the
// property is logged once daily. If the backend ever changes that basis, this
// is the only place that needs to know.
//
// Flooring predictedVolume at 0 keeps the answer physical: a tank that runs dry
// before the dispatch date needs a full load to the max fill line, never more.
// A site with no usable Injection Avg predicts as its current fillAmount rather
// than dropping out, so totals stay summable.

const MS_PER_DAY = 86400000;

/**
 * Whole days from today to the dispatch date, both taken at local midnight so a
 * same-day dispatch is 0 rather than a fraction. A missing or past date gives 0
 * — the forecast never runs backwards.
 */
export function daysUntil(dispatchDate, now = new Date()) {
  if (dispatchDate == null || dispatchDate === '') return 0;
  const target = new Date(dispatchDate);
  if (isNaN(target.getTime())) return 0;
  const targetDay = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((targetDay - today) / MS_PER_DAY));
}

/**
 * Current and predicted litres-to-deliver for one site, as a range.
 *
 * A site does not have one delivery figure, it has a minimum and a maximum:
 *   minFillAmount = what it takes to reach the site's MINIMUM volume
 *                   (capacity x minFillPct/100)
 *   fillAmount    = what it takes to reach the site's MAXIMUM fill level
 *                   (capacity x maxFillPct/100)
 * Anything between the two is an acceptable drop. minFillAmount is 0 whenever
 * the tank is already at or above its minimum level.
 *
 * `fillAmount` keeps its name (rather than becoming maxFillAmount) because the
 * delivery planner, the summary and the email all already read it as the amount
 * to deliver.
 *
 * @param {object} input
 * @param {*} input.capacity      tank capacity (litres)
 * @param {*} input.volume        current volume (litres)
 * @param {*} input.injectionAvg  average daily draw (litres/day); missing = no growth
 * @param {number} input.minFillPct  the site's own Minimum Fill, as a percentage
 *   of capacity. Missing/unusable is treated as 0 -- no minimum.
 * @param {number} input.maxFillPct  fill ceiling as a percentage; the site's own
 *   Max Fill property, defaulted by the caller when the site has no usable value
 * @param {number} input.days     days until the dispatch date
 * @returns {{minFillAmount: number|null, fillAmount: number|null,
 *   predictedMinFillAmount: number|null, predictedFillAmount: number|null,
 *   injectionAvg: number|null}}
 *   The amounts are null when capacity or volume is unusable — the caller decides
 *   how to present "unknown".
 */
export function computeFillAmounts({ capacity, volume, injectionAvg, minFillPct, maxFillPct, days }) {
  const rate = parseFloat(injectionAvg);
  const usableRate = isNaN(rate) || rate <= 0 ? 0 : rate;

  const cap = parseFloat(capacity);
  const vol = parseFloat(volume);
  if (isNaN(cap) || isNaN(vol)) {
    return {
      minFillAmount: null,
      fillAmount: null,
      predictedMinFillAmount: null,
      predictedFillAmount: null,
      injectionAvg: isNaN(rate) ? null : rate,
    };
  }

  const rawMin = parseFloat(minFillPct);
  const minFillVolume = cap * ((isNaN(rawMin) ? 0 : rawMin) / 100);
  const maxFillVolume = cap * (Number(maxFillPct) / 100);
  const forecastDays = Math.max(0, Number(days) || 0);
  const predictedVolume = Math.max(0, vol - usableRate * forecastDays);

  // Floored at 0 for the same reason predictedVolume is: a site already at or
  // above a fill line needs nothing, and a negative litres-to-deliver is not
  // a physical answer. Keeps totals summable across a run.
  return {
    minFillAmount: Math.max(0, Math.round(minFillVolume - vol)),
    fillAmount: Math.max(0, Math.round(maxFillVolume - vol)),
    predictedMinFillAmount: Math.max(0, Math.round(minFillVolume - predictedVolume)),
    predictedFillAmount: Math.max(0, Math.round(maxFillVolume - predictedVolume)),
    injectionAvg: isNaN(rate) ? null : rate,
  };
}
